import { setup, assign, fromPromise, fromCallback, emit } from "xstate";
import { createActorContext } from "@xstate/react";
import { PreviewRequest } from "../workers/las.worker";
import { LasWorkerPool } from "../workers/worker-pool";
import {
  ProcessedFile,
  PointCloudData,
  MachineContext,
  FileProcessingEvent,
  isProcessedFileUploaded,
} from "./types";
import { createUploads, deleteUploads } from "./uploadMachine/services";
import { createBrowserInspector } from "@statelyai/inspect";
import { generateUniqueFileIdentifier } from "./uploadMachine";

function computeMissingFlag(upload: ProcessedLidar): boolean {
  if (isProcessedFileUploaded(upload)) {
    return false;
  }

  return !(upload.file instanceof File);
}

type ProcessedLidar = ProcessedFile<PointCloudData>;

interface LidarContext extends MachineContext<ProcessedLidar> {
  processing: string[];
}

type LidarEvent = FileProcessingEvent;

let workerPool: LasWorkerPool | null = null;

function getWorkerPool(): LasWorkerPool {
  if (!workerPool) {
    workerPool = new LasWorkerPool();
  }
  return workerPool;
}

export function cleanupLidarWorkerPool() {
  if (workerPool) {
    workerPool.terminate();
    workerPool = null;
  }
}

async function processLidarFiles({
  input,
}: {
  input: { files: File[]; context?: LidarContext };
}) {
  const output: ProcessedLidar[] = [];
  const context = input.context;

  for (const file of input.files) {
    const extension = file.name.toLowerCase().split(".").pop();

    if (extension === "las" || extension === "laz") {
      const id = generateUniqueFileIdentifier(file).name;

      const existingUpload = context.uploads.find(
        (upload) => upload.id === id && isProcessedFileUploaded(upload)
      );

      if (!existingUpload) {
        const newUpload = {
          id,
          file,
          progress: 0,
        };
        output.push({
          ...newUpload,
          missing: computeMissingFlag(newUpload),
        });
      }
    }
  }

  return output;
}

const processLidarFile = ({
  input,
  sendBack,
}: {
  input: { file: File; id: string };
  sendBack: (event: LidarEvent) => void;
}) => {
  const { file, id } = input;

  const request: PreviewRequest = {
    file,
    targetPoints: 1_000_000,
    chunkBytes: 8 << 20,
  };

  getWorkerPool()
    .process(request, (progress) => {
      if (progress.type === "progress") {
        const progressValue = progress.totalBytes
          ? progress.readBytes / progress.totalBytes
          : 0;
        sendBack({
          type: "PROCESSING_PROGRESS",
          id,
          progress: progressValue,
        });
      }
    })
    .then(
      (result: {
        positions: Float32Array;
        bbox: [number, number, number, number, number, number];
        times?: Float64Array;
        colors?: Uint8Array;
        totalPoints: number;
        coordinateInfo: {
          origin: [number, number, number];
        };
      }) => {
        let timestamps: [number, number] | undefined;

        if (result.times) {
          const sortedTimes = Array.from(result.times).sort(
            (a: number, b: number) => a - b
          );
          const startTime = sortedTimes[0];
          const endTime = sortedTimes[sortedTimes.length - 1];

          const GPS_EPOCH_MS = Date.UTC(1980, 0, 6, 0, 0, 0);

          const startUnixMs = Math.round(
            GPS_EPOCH_MS + (startTime - 18) * 1000
          );

          const endUnixMs = Math.round(GPS_EPOCH_MS + (endTime - 18) * 1000);

          timestamps = [startUnixMs, endUnixMs];
        }

        sendBack({
          type: "PROCESSING_COMPLETE",
          id,
          data: {
            positions: result.positions,
            colors: result.colors,
            origin: result.coordinateInfo.origin,
            bbox: result.bbox,
            points: result.totalPoints,
            timestamps,
          },
        });
      }
    )
    .catch((error) => {
      sendBack({
        type: "PROCESSING_ERROR",
        id,
        error: error instanceof Error ? error.message : String(error),
      });
    });
};

async function createLidarUploads({
  input,
}: {
  input: { lidarFiles: ProcessedLidar[]; surveyId: number; orderId: number };
}) {
  const { lidarFiles, surveyId, orderId } = input;

  if (lidarFiles.length === 0) {
    return { created: [] };
  }

  const created = await createUploads({
    input: lidarFiles.map((lidar) => ({
      key: lidar.id,
      fileName: lidar.file.name,
      fileSize: lidar.file.size,
      fileType: lidar.file.type,
      fileModifiedAt: lidar.file.lastModified,
      fileExtension: lidar.file.name.split(".").pop()?.toLowerCase() || "",
      fileMetadata: lidar.data
        ? {
            bbox: lidar.data.bbox,
            points: lidar.data.points,
            timestamps: lidar.data.timestamps,
          }
        : {},
      surveyId,
      orderId,
    })),
  });

  return { created };
}

async function deleteLidarUploads({
  input,
}: {
  input: { keys: string[]; surveyId: number; orderId: number };
}) {
  const { keys, surveyId, orderId } = input;

  if (keys.length === 0) {
    return { deleted: [] };
  }

  const deleted = await deleteUploads({ input: { keys, surveyId, orderId } });

  return { deleted };
}

export const createLidarMachine = setup({
  types: {} as {
    context: LidarContext;
    events: LidarEvent;
    input: { existingUploads?: any[]; surveyId?: number; orderId?: number };
    emitted: { type: "focusOnLidar"; bbox: number[] };
  },
  actors: {
    processLidarFiles: fromPromise(processLidarFiles),
    processLidarFile: fromCallback<LidarEvent, { file: File; id: string }>(
      processLidarFile
    ),
    createLidarUploads: fromPromise(createLidarUploads),
    deleteLidarUploads: fromPromise(deleteLidarUploads),
  },
}).createMachine({
  id: "manageLidar",
  initial: "idle",
  context: ({ input }) =>
    ({
      uploads:
        input?.existingUploads?.map((upload) => {
          const processedUpload = {
            id: upload.key,
            file: {
              size: upload.file_size,
              name: upload.file_name,
              type: upload.file_type,
              lastModified: upload.file_modified_at,
            },
            data: upload.file_metadata,
            status: upload.status,
            progress: 1,
          };
          return {
            ...processedUpload,
            missing: computeMissingFlag(processedUpload),
          };
        }) || [],
      active: [],
      processing: [],
      removing: null,
      surveyId: input?.surveyId ?? 0,
      orderId: input?.orderId ?? 0,
    } as LidarContext),
  states: {
    idle: {
      entry: () => {
        cleanupLidarWorkerPool();
      },
      always: [
        {
          guard: ({ context }) =>
            context.uploads.some((upload) => upload.data?.bbox),
          actions: emit(({ context }) => ({
            type: "focusOnLidar",
            bbox:
              context.uploads.find((upload) => upload.data?.bbox)?.data?.bbox ??
              [],
          })),
        },
      ],
      on: {
        ADD_FILES: {
          target: "identify",
          guard: ({ event }: { event: { files: File[] } }) =>
            event.files.length > 0,
        },
        REMOVE_FILE: {
          target: "remove",
          actions: assign({
            removing: ({ context, event }) => event.id,
          }),
        },
        REMOVE_MISSING: {
          target: "removeMissing",
        },
      },
    },
    remove: {
      initial: "confirm",
      states: {
        confirm: {
          on: {
            CONFIRM_REMOVE: { target: "removing" },
            CANCEL_REMOVE: { target: "#manageLidar.idle" },
          },
        },
        removing: {
          invoke: {
            id: "deleteLidarUploads",
            src: "deleteLidarUploads",
            input: ({ context }) => ({
              keys: [context.removing],
              surveyId: context.surveyId,
              orderId: context.orderId,
            }),
            onDone: {
              target: "#manageLidar.idle",
              actions: [
                assign({
                  removing: null,
                  uploads: ({ context }) =>
                    context.uploads.filter((u) => u.id !== context.removing),
                }),
                emit({ type: "focusOnLidar", bbox: [] }),
              ],
            },
            onError: {
              target: "#manageLidar.idle",
              actions: assign({
                removing: null,
              }),
            },
          },
        },
      },
    },
    removeMissing: {
      initial: "confirm",
      states: {
        confirm: {
          on: {
            CONFIRM_REMOVE_MISSING: { target: "removing" },
            CANCEL_REMOVE: { target: "#manageLidar.idle" },
          },
        },
        removing: {
          invoke: {
            id: "deleteMissingLidarUploads",
            src: "deleteLidarUploads",
            input: ({ context }) => ({
              keys: context.uploads
                .filter((upload) => upload.missing)
                .map((upload) => upload.id),
              surveyId: context.surveyId,
              orderId: context.orderId,
            }),
            onDone: {
              target: "#manageLidar.idle",
              actions: [
                assign({
                  uploads: ({ context }) =>
                    context.uploads.filter((upload) => !upload.missing),
                  removing: null,
                }),
                emit({ type: "focusOnLidar", bbox: [] }),
              ],
            },
            onError: {
              target: "#manageLidar.idle",
              actions: assign({
                removing: null,
              }),
            },
          },
        },
      },
    },
    identify: {
      invoke: {
        id: "processLidarFiles",
        src: "processLidarFiles",
        input: ({ event, context }) => {
          if (event.type === "ADD_FILES") {
            return { files: event.files, context };
          }
          throw new Error("Invalid event type");
        },
        onDone: [
          {
            guard: ({ event }) => event.output.length > 0,
            target: "processing",
            actions: assign({
              uploads: ({ context, event }) => {
                const existing = new Map(event.output.map((u) => [u.id, u]));

                return [
                  ...context.uploads.map((u) => {
                    const updated = existing.get(u.id) ?? u;
                    return {
                      ...updated,
                      missing: computeMissingFlag(updated),
                    };
                  }),
                  ...event.output.filter(
                    (u) =>
                      !context.uploads.some((existing) => existing.id === u.id)
                  ),
                ];
              },
              processing: ({ event }) =>
                event.output.map((lidar: ProcessedLidar) => lidar.id),
            }),
          },
          {
            target: "idle",
          },
        ],
        onError: {
          target: "idle",
        },
      },
    },
    processing: {
      initial: "check",
      states: {
        check: {
          always: [
            {
              guard: ({ context }) => {
                return (
                  context.uploads.filter(
                    (upload) => !upload.data && !upload.error
                  ).length > 0
                );
              },
              target: "start",
            },
            { target: "#manageLidar.creating" },
          ],
        },
        start: {
          entry: assign({
            active: ({ context }) =>
              context.uploads
                .filter((upload) => !upload.data && !upload.error)
                .map((upload) => upload.id),
          }),
          invoke: {
            id: "processLidarFile",
            src: "processLidarFile",
            input: ({ context }) => {
              const activeUpload = context.uploads.find(
                (upload) => upload.id === context.active[0]
              );

              if (activeUpload === undefined) {
                throw new Error("Active upload not found");
              }

              return {
                file:
                  activeUpload.file instanceof File
                    ? activeUpload.file
                    : new File([], activeUpload.file.name, {
                        type: activeUpload.file.type,
                      }),
                id: activeUpload.id,
              };
            },
          },
          on: {
            PROCESSING_PROGRESS: {
              actions: assign({
                uploads: ({ context, event }) =>
                  context.uploads.map((upload) =>
                    upload.id === event.id
                      ? {
                          ...upload,
                          progress: event.progress,
                        }
                      : upload
                  ),
              }),
            },
            PROCESSING_COMPLETE: {
              target: "check",
              actions: [
                assign({
                  uploads: ({ context, event }) =>
                    context.uploads.map((upload) =>
                      upload.id === event.id
                        ? {
                            ...upload,
                            data: event.data,
                            progress: 1,
                          }
                        : upload
                    ),
                  active: null,
                }),
              ],
            },
            PROCESSING_ERROR: {
              target: "check",
              actions: assign({
                uploads: ({ context, event }) =>
                  context.uploads.map((upload) =>
                    upload.id === event.id
                      ? {
                          ...upload,
                          error: event.error,
                        }
                      : upload
                  ),
                active: null,
              }),
            },
          },
        },
      },
    },
    creating: {
      invoke: {
        id: "createLidarUploads",
        src: "createLidarUploads",
        input: ({ context }: { context: LidarContext }) => {
          return {
            lidarFiles: context.uploads.filter((upload) =>
              context.processing.includes(upload.id)
            ),
            surveyId: context.surveyId,
            orderId: context.orderId,
          };
        },
        onDone: {
          target: "idle",
          actions: emit({ type: "focusOnLidar", bbox: [] }),
        },
        onError: {
          target: "idle",
        },
      },
    },
  },
});

export const LidarMachineContext = createActorContext(createLidarMachine, {});
